import React, { useEffect, useState } from "react";
import {
    Navbar,
    NavbarBrand,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from "reactstrap";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useUser } from "./Context/UserContext";
import { useTheme } from "./Context/ThemeContext";
import { useUI } from "./Context/UIContext";
import Profile, { getInitials } from "./Profile";
import Logo from "./ui/Logo";
import Icon from "./ui/Icon";
import "../navmenu.css";

export default function NavMenu() {
    const { userInfo, menus, logoutUser } = useUser();
    const { theme, toggleTheme } = useTheme();
    const { toggleSidebar, sidebarOpen } = useUI();
    const location = useLocation();
    const history = useHistory();
    const [scrolled, setScrolled] = useState(false);

    const handleLogout = () => {
        logoutUser();
        // replace, not push: the page we are leaving must not stay in the back stack
        history.replace("/login");
    };

    // Elevate the glass bar only once content scrolls beneath it.
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 4);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Current page name for the breadcrumb (derived from the menu the user is on).
    const pathname = (location.pathname || "/").toLowerCase();
    const pageLabel = (() => {
        if (pathname.startsWith("/employee/")) return "Employee Review";
        const match = (menus || []).find(
            m => m.routePath && m.routePath.toLowerCase() === pathname
        );
        if (match) return match.menuName;
        if (pathname === "/" || pathname === "/home") return "Home";
        return "Overview";
    })();

    return (
        <Navbar className={`custom-navbar ${scrolled ? "nav-scrolled" : ""}`} fixed="top">
            <div className="navbar-inner">
                <div className="navbar-left">
                    {/* Mobile drawer toggle — morphing hamburger, not an icon swap */}
                    <button
                        type="button"
                        className={`hamburger nav-drawer-toggle ${sidebarOpen ? "is-open" : ""}`}
                        onClick={toggleSidebar}
                        aria-label={sidebarOpen ? "Close navigation menu" : "Open navigation menu"}
                        aria-expanded={sidebarOpen}
                        aria-controls="app-sidebar"
                    >
                        <span className="hamburger-box" aria-hidden="true">
                            <span className="hamburger-line l1" />
                            <span className="hamburger-line l2" />
                            <span className="hamburger-line l3" />
                        </span>
                    </button>

                    {/* Logo shows only when the sidebar (which owns the brand) is hidden */}
                    <NavbarBrand tag={Link} to="/home" className="navbar-brand-mt">
                        <Logo size={24} />
                    </NavbarBrand>

                    {/* Desktop breadcrumb */}
                    <nav className="nav-breadcrumb" aria-label="Breadcrumb">
                        <span className="crumb-root">Workspace</span>
                        <Icon name="chevronRight" size={14} className="crumb-sep" />
                        <span className="crumb-page" aria-current="page">{pageLabel}</span>
                    </nav>
                </div>

                <div className="navbar-right">
                    <button
                        type="button"
                        className="icon-btn theme-toggle"
                        onClick={toggleTheme}
                        aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                    >
                        {/* key remount replays the pop-in morph on every switch */}
                        <span className="theme-icon" key={theme}>
                            <Icon name={theme === "dark" ? "sun" : "moon"} size={19} />
                        </span>
                    </button>

                    {userInfo && (
                        <UncontrolledDropdown nav inNavbar className="profile-dropdown-wrap">
                            <DropdownToggle nav className="profile-toggle">
                                {userInfo.profileImg ? (
                                    <img
                                        src={userInfo.profileImg}
                                        alt=""
                                        className="profile-img-sm"
                                    />
                                ) : (
                                    <span className="nav-avatar" aria-hidden="true">
                                        {getInitials(userInfo.name)}
                                    </span>
                                )}
                                <span className="profile-toggle-name">{userInfo.name}</span>
                                <Icon name="chevronDown" size={15} className="profile-caret" />
                            </DropdownToggle>

                            <DropdownMenu right className="profile-dropdown">
                                <Profile />
                                <DropdownItem divider />
                                <DropdownItem onClick={handleLogout} className="logout-menu-item">
                                    <Icon name="logout" size={16} />
                                    <span>Sign out</span>
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    )}
                </div>
            </div>
        </Navbar>
    );
}
